import { useState } from "react";
import { Download, LogOut, Loader2 } from "lucide-react";
import { Wordmark } from "@/components/site/Nav";
import { Overview, BookingsTable, WaitlistTable } from "@/components/admin/Tables";
import { useAdminSession, useAdminData, downloadCsv, filterBookings } from "@/hooks/useAdmin";
import { api, errorMessage } from "@/lib/api";

const Login = ({ onDone }) => {
  const [pw, setPw] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const go = async (e) => {
    e.preventDefault(); setBusy(true); setErr("");
    try { await api.post("/admin/login", { password: pw }); onDone(); } catch (x) { setErr(errorMessage(x)); } finally { setBusy(false); }
  };
  return (
    <main className="container-x py-20 max-w-md" data-testid="admin-login">
      <p className="eyebrow mb-3">Administrator</p>
      <h1 className="h-section">Sign in</h1>
      <form onSubmit={go} className="mt-8 space-y-4">
        <div>
          <label htmlFor="pw" className="label">Password</label>
          <input id="pw" type="password" className={`field ${err ? "field-error" : ""}`} value={pw} onChange={(e) => setPw(e.target.value)} autoComplete="current-password" data-testid="admin-password" />
          {err && <p className="error-text" role="alert" data-testid="admin-login-error">{err}</p>}
        </div>
        <button type="submit" className="btn-primary w-full" disabled={busy || !pw} data-testid="admin-login-submit">{busy ? <Loader2 size={18} className="animate-spin" /> : "Sign in"}</button>
      </form>
    </main>
  );
};

const Toolbar = ({ tab, setTab, q, setQ }) => (
  <div className="mt-8 flex flex-wrap items-center justify-between gap-3 rule pt-6">
    <div className="flex gap-2" role="tablist">
      {["bookings", "waitlist"].map((t) => <button key={t} role="tab" aria-selected={tab === t} onClick={() => setTab(t)} className={`chip !min-h-[40px] !py-2 capitalize ${tab === t ? "chip-active" : "chip-idle"}`} data-testid={`admin-tab-${t}`}>{t}</button>)}
    </div>
    <div className="flex items-center gap-3">
      {tab === "bookings" && <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name, email, reference…" className="field !min-h-[40px] !py-2 w-64 text-sm" aria-label="Search bookings" data-testid="admin-search" />}
      <button type="button" onClick={() => downloadCsv(`/admin/export/${tab}.csv`, `unseen-${tab}.csv`)} className="btn-secondary !min-h-[40px] !py-2 text-sm" data-testid="admin-export"><Download size={14} /> Export CSV</button>
    </div>
  </div>
);

const Dashboard = ({ onLogout }) => {
  const [tab, setTab] = useState("bookings");
  const [q, setQ] = useState("");
  const { overview, bookings, waitlist, setStatus } = useAdminData();
  const rows = tab === "bookings" ? filterBookings(bookings, q) : waitlist;
  return (
    <main className="container-x py-10" data-testid="admin-dashboard">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div><p className="eyebrow mb-2">Administrator</p><h1 className="h-section">Bookings & waitlist</h1></div>
        <button type="button" onClick={onLogout} className="btn-secondary !min-h-[40px] !py-2 text-sm" data-testid="admin-logout"><LogOut size={14} /> Sign out</button>
      </div>
      {overview && <Overview ov={overview} />}
      <Toolbar tab={tab} setTab={setTab} q={q} setQ={setQ} />
      <div className="mt-4 overflow-x-auto">
        {tab === "bookings" ? <BookingsTable rows={rows} onStatus={setStatus} /> : <WaitlistTable rows={rows} />}
        {!rows.length && <p className="py-10 text-sm text-sec text-center">Nothing here yet.</p>}
      </div>
    </main>
  );
};

export default function Admin() {
  const { authed, setAuthed, logout } = useAdminSession();
  return (
    <div className="min-h-screen bg-white" data-testid="admin-page">
      <header className="border-b border-divider"><div className="container-x h-16 flex items-center justify-between"><Wordmark /><span className="mono text-sec">Admin</span></div></header>
      {authed === null ? <p className="container-x py-20 text-sm text-sec">Loading…</p> : authed ? <Dashboard onLogout={logout} /> : <Login onDone={() => setAuthed(true)} />}
    </div>
  );
}
